import type { TFunction } from 'i18next';
import { decide, type AllergyResponse } from '@emr/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listAllergies } from '../../../api/chart.ts';
import { useGrants } from '../../../auth/sessionContext.ts';
import { Button } from '../../../components/primitives.tsx';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import { ChartTable } from './ChartTable.tsx';
import { AddAllergyDialog } from './AddAllergyDialog.tsx';

// `substance`, `reaction`, `severity`, and `status` are all open free text (`optionalTrimmed`/
// `trimmed(MAX_NAME_LENGTH)` or `MAX_FREE_TEXT_LENGTH`), none a closed enum — every one gets a
// `maxWidthCh` cap, not just the ones that look longest, per `DataTable.tsx`'s own header comment.
function columnsFor(t: TFunction<'patientChartTabs'>): readonly DataTableColumn<AllergyResponse>[] {
  return [
    {
      key: 'substance',
      header: t('allergies.columns.substance'),
      cell: (allergy) => allergy.substance,
      wrap: 'anywhere',
      maxWidthCh: 22,
    },
    {
      key: 'reaction',
      header: t('allergies.columns.reaction'),
      cell: (allergy) => allergy.reaction ?? '—',
      wrap: 'anywhere',
      maxWidthCh: 28,
    },
    {
      key: 'severity',
      header: t('allergies.columns.severity'),
      cell: (allergy) => allergy.severity ?? '—',
      wrap: 'wrap',
      maxWidthCh: 12,
    },
    {
      key: 'status',
      header: t('allergies.columns.status'),
      cell: (allergy) => allergy.status,
      wrap: 'wrap',
      maxWidthCh: 12,
    },
  ];
}

export function AllergiesTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {mayWrite ? (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            {t('allergies.addButton')}
          </Button>
        </div>
      ) : null}

      <ChartTable
        queryKey={['patient', patientId, 'allergies']}
        queryFn={(signal) => listAllergies(patientId, signal)}
        columns={columnsFor(t)}
        rowKey={(allergy) => allergy.id}
        minWidthRem={40}
        emptyMessage={t('allergies.empty')}
        loadingLabel={t('allergies.loading')}
        caption={t('allergies.caption')}
      />

      {mayWrite ? (
        <AddAllergyDialog
          patientId={patientId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSaved={() => undefined}
        />
      ) : null}
    </div>
  );
}
